import { FieldValidator } from 'final-form';
import { useEffect, useRef, useState } from 'react';
import { useField } from 'react-final-form';

import { useDuplicateBookingCheck } from '../../../hooks/useDuplicateBookingCheck';
import { DuplicateBookingData } from '../../../types/duplicateBooking';
import DuplicateBookingWarning from '../../DuplicateBookingWarning';

import ClientTeamMemberSelect, {
  ClientTeamMemberSelectOption,
} from './ClientTeamMemberSelect';

interface Props {
  name: string;
  validate?: FieldValidator<ClientTeamMemberSelectOption | ''>;
  onAdd?: () => void;
  testId?: string;
  disabled?: boolean;
  allowCreateGuest?: boolean;
  checkInDate?: string;
  checkOutDate?: string;
  onDuplicateStatusChange?: (
    fieldName: string,
    hasDuplicates: boolean,
    data?: DuplicateBookingData,
  ) => void;
}

export default function ClientTeamMemberSelectWithDuplicateCheck({
  name,
  validate,
  onAdd,
  testId,
  disabled = false,
  allowCreateGuest = false,
  checkInDate,
  checkOutDate,
  onDuplicateStatusChange,
}: Props) {
  const { input } = useField(name);
  const { checkForDuplicates } = useDuplicateBookingCheck();
  const [duplicateResult, setDuplicateResult] =
    useState<DuplicateBookingData | null>(null);

  // Use ref to store latest callback to avoid dependency issues
  const onDuplicateStatusChangeRef = useRef(onDuplicateStatusChange);
  onDuplicateStatusChangeRef.current = onDuplicateStatusChange;

  const currentValue = input.value as ClientTeamMemberSelectOption | '';

  useEffect(() => {
    // Check for duplicates when guest is selected and we have required data
    const checkDuplicates = async () => {
      if (
        currentValue &&
        currentValue.id &&
        checkInDate &&
        checkOutDate &&
        !currentValue.unknownGuest
      ) {
        try {
          const result = await checkForDuplicates(
            currentValue.id,
            checkInDate,
            checkOutDate,
          );
          const hasDuplicates = !!result?.hasOverlappingBookings;
          setDuplicateResult(hasDuplicates ? result : null);
          // Notify parent component about duplicate status
          if (onDuplicateStatusChangeRef.current) {
            onDuplicateStatusChangeRef.current(name, hasDuplicates, result);
          }
        } catch (error) {
          console.error('Error checking for duplicate bookings:', error);
          setDuplicateResult(null);
          if (onDuplicateStatusChangeRef.current) {
            onDuplicateStatusChangeRef.current(name, false);
          }
        }
      } else {
        setDuplicateResult(null);
        if (onDuplicateStatusChangeRef.current) {
          onDuplicateStatusChangeRef.current(name, false);
        }
      }
    };

    checkDuplicates();
  }, [currentValue, checkInDate, checkOutDate, checkForDuplicates, name]);

  const handleAdd = () => {
    // Reset warning when guest changes
    setDuplicateResult(null);
    onAdd?.();
  };

  return (
    <div>
      <ClientTeamMemberSelect
        name={name}
        validate={validate}
        onAdd={handleAdd}
        testId={testId}
        disabled={disabled}
        allowCreateGuest={allowCreateGuest}
      />
      {duplicateResult && (
        <DuplicateBookingWarning
          duplicateData={duplicateResult}
          className="mt-3"
        />
      )}
    </div>
  );
}
